import React, { useEffect, useRef, useState } from 'react';
import { EXPORT_COUNTRIES } from '../mock/mock';

const MAP_ASPECT_H = 65.26;   // matches viewBox of world-map.svg (620 / 950)
const CYCLE_MS = 2200;        // magnify each country for ~2.2s

/**
 * GlobalPresenceMap
 * World map with a flag pin per delivery country.
 * Continuously magnifies one country at a time (flag + shape spotlight),
 * with leader lines fanning European flags out so every flag is clearly visible.
 */
const GlobalPresenceMap = () => {
  const [focusIndex, setFocusIndex] = useState(0);
  const [hoverIndex, setHoverIndex] = useState(null);
  const timerRef = useRef(null);

  // Auto-cycle magnification (paused while user hovers a pin)
  useEffect(() => {
    if (hoverIndex !== null) return;
    timerRef.current = setTimeout(() => {
      setFocusIndex(i => (i + 1) % EXPORT_COUNTRIES.length);
    }, CYCLE_MS);
    return () => clearTimeout(timerRef.current);
  }, [focusIndex, hoverIndex]);

  const activeIndex = hoverIndex !== null ? hoverIndex : focusIndex;

  return (
    <div className="relative w-full" data-testid="global-presence-map">
      <div className="relative w-full" style={{ paddingBottom: `${MAP_ASPECT_H}%` }}>
        {/* World map backdrop */}
        <img
          src="/world-map.svg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-55"
          style={{ filter: 'sepia(0.32) brightness(0.94) contrast(0.92)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#faf6f0]/25 via-transparent to-[#e8dfd1]/25 pointer-events-none" />

        {/* Spotlight — soft radial glow follows the active country */}
        {activeIndex != null && (() => {
          const c = EXPORT_COUNTRIES[activeIndex];
          const cx = c.x;
          const cy = c.y;
          return (
            <div
              key={`spot-${activeIndex}`}
              className="absolute pointer-events-none rcd-spot-fade"
              style={{
                left:  `${cx - 12}%`,
                top:   `${cy - 12}%`,
                width:  '24%',
                paddingBottom: '24%',
                background: 'radial-gradient(circle, rgba(166,124,61,0.35) 0%, rgba(166,124,61,0) 65%)',
                filter: 'blur(6px)'
              }}
            />
          );
        })()}

        {/* Leader lines from each marker dot to its offset flag tag */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox={`0 0 100 ${MAP_ASPECT_H}`}
          preserveAspectRatio="none"
        >
          {EXPORT_COUNTRIES.map((c, i) => {
            if (c.tagX == null || c.tagY == null) return null;
            return (
              <line
                key={`leader-${i}`}
                x1={c.x}
                y1={(c.y * MAP_ASPECT_H) / 100}
                x2={c.tagX}
                y2={(c.tagY * MAP_ASPECT_H) / 100}
                stroke="#a67c3d"
                strokeWidth={activeIndex === i ? 0.25 : 0.13}
                strokeDasharray="0.6 0.5"
                opacity={activeIndex === i ? 0.85 : 0.5}
              />
            );
          })}
        </svg>

        {/* Location dots at the true country coordinates */}
        {EXPORT_COUNTRIES.map((c, i) => {
          const isActive = i === activeIndex;
          const isHome = !!c.home;
          return (
            <span
              key={`dot-${i}`}
              className={`absolute rounded-full ring-2 ring-[#faf6f0] transition-all duration-500 ${
                isActive ? 'scale-150' : 'scale-100'
              }`}
              style={{
                left: `${c.x}%`,
                top:  `${c.y}%`,
                width:  isHome || isActive ? 14 : 10,
                height: isHome || isActive ? 14 : 10,
                transform: 'translate(-50%, -50%)',
                background: '#a67c3d',
                boxShadow: isActive
                  ? '0 0 0 6px rgba(166,124,61,0.22), 0 6px 16px -4px rgba(26,22,20,0.35)'
                  : '0 2px 4px rgba(26,22,20,0.25)',
                zIndex: isActive ? 15 : 5
              }}
            />
          );
        })}

        {/* Flag chips — positioned at tagX/tagY (or country point if no offset) */}
        {EXPORT_COUNTRIES.map((c, i) => {
          const isActive = i === activeIndex;
          const isHome = !!c.home;
          const px = c.tagX ?? c.x;
          const py = c.tagY ?? c.y - 5;
          return (
            <div
              key={`chip-${i}`}
              className="absolute -translate-x-1/2 -translate-y-1/2 transition-all duration-[700ms] ease-out"
              style={{
                left: `${px}%`,
                top:  `${py}%`,
                zIndex: isActive ? 30 : 12,
                transform: `translate(-50%, -50%) scale(${isActive ? 1.35 : 1})`,
              }}
              onMouseEnter={() => setHoverIndex(i)}
              onMouseLeave={() => setHoverIndex(h => (h === i ? null : h))}
              data-testid={`presence-pin-${c.short.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div
                className={`relative flex items-center gap-1.5 md:gap-2 px-1.5 md:px-2 py-1 md:py-1.5 bg-[#faf6f0] shadow-md transition-all duration-500 ${
                  isActive
                    ? 'border-2 border-[#a67c3d] ring-4 ring-[#a67c3d]/25 shadow-2xl'
                    : 'border border-[#a67c3d]/60'
                } ${isHome && !isActive ? 'ring-2 ring-[#a67c3d]/30' : ''}`}
              >
                <img
                  src={c.flag}
                  alt={`${c.name} flag`}
                  loading="lazy"
                  className={`object-cover transition-all duration-500 ${
                    isActive
                      ? 'w-9 h-6 md:w-11 md:h-7'
                      : 'w-6 h-4 md:w-7 md:h-4.5'
                  }`}
                />
                <span
                  className={`font-serif text-[#1a1614] whitespace-nowrap transition-all duration-500 ${
                    isActive ? 'text-sm md:text-base pr-1 md:pr-1.5' : 'text-[10px] md:text-xs pr-0.5 md:pr-1'
                  }`}
                >
                  {c.short}
                </span>
              </div>

              {/* Home / role tag under active flag */}
              {isActive && (
                <div
                  key={`role-${i}`}
                  className="absolute left-1/2 -translate-x-1/2 top-full mt-2 text-center pointer-events-none rcd-caption-fade"
                >
                  <p className="text-[9px] tracking-[0.28em] uppercase text-[#a67c3d] whitespace-nowrap">
                    {c.label}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend + progress dots */}
      <div className="mt-6 md:mt-8 flex flex-col items-center gap-4">
        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-xs text-[#4a3f36]">
          <span className="flex items-center gap-2">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#a67c3d] ring-2 ring-[#faf6f0]" />
            Headquarters &mdash; Jaipur, India
          </span>
          <span className="flex items-center gap-2">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#a67c3d]/80 ring-2 ring-[#faf6f0]" />
            Export markets &mdash; {EXPORT_COUNTRIES.filter(c => !c.home).length} countries
          </span>
        </div>

        {/* Country progress dots — click to jump */}
        <div className="flex items-center gap-1.5">
          {EXPORT_COUNTRIES.map((c, i) => (
            <button
              key={`prog-${i}`}
              onClick={() => { setFocusIndex(i); setHoverIndex(null); }}
              aria-label={`Focus ${c.name}`}
              data-testid={`presence-dot-${i}`}
              className={`h-[3px] transition-all duration-500 ${
                i === activeIndex ? 'w-8 bg-[#a67c3d]' : 'w-3 bg-[#a67c3d]/25 hover:bg-[#a67c3d]/50'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default GlobalPresenceMap;
